import axios from "axios";

// Tipos para o serviço de Balanço
export type ProdutoBalancoType = {
  produtoId: number;
  produtoCodigo: number;
  nomeProduto: string;
  fabricante?: string;
  lote?: string;
  validade?: string;
  quantidade: number;
  estoqueAtual: number;
};

export type BalancoType = {
  id: number;
  data: string;
  finalizado: boolean;
  dataFinalizacao?: string;
  produtos: ProdutoBalancoType[];
};

export type DepartamentoType = {
  id: number;
  descricao: string;
};

export type ProdutoParaBalancoType = {
  id: number;
  nome: string;
  codigo: number;
  codigoBarras?: string;
  fabricante?: string;
  controleEspecial: boolean;
  estoqueAtual: number;
  unidade?: string;
  jaNoBalanco: boolean;
  departamentoId?: number;
  departamentoDescricao?: string;
};

export type CriarBalancoDto = {
  // Sem campos específicos - criação simples
};

export type IncluirProdutoBalancoDto = {
  produtoId: number;
  quantidade: number;
  lote?: string;
  validade?: string;
};

export type AtualizarProdutoBalancoDto = {
  produtoId: number;
  lote?: string;
  novaQuantidade: number;
};

export type ExcluirProdutoBalancoDto = {
  produtoId: number;
  lote?: string;
};

export type ZerarEstoqueDto = {
  departamentosIds: number[];
  zerarTodos: boolean;
  apenasControleEspecial: boolean;
};



// Função para criar o config com header empresaId
const createConfig = (empresaId: number) => ({
  headers: {
    empresaId: empresaId.toString()
  }
});

// Serviços para Balanço
export const balancoService = {
  // === BALANÇO ===
  async obterBalancoAtivo(empresaId: number): Promise<BalancoType> {
    const response = await axios.get("/api/balanco", createConfig(empresaId));
    return response.data;
  },

  async criarBalanco(dto: CriarBalancoDto, empresaId: number): Promise<void> {
    try {
      console.log('Enviando para backend:', dto, 'EmpresaId:', empresaId);
      const response = await axios.post("/api/balanco", dto, createConfig(empresaId));
      console.log('Resposta do backend (sucesso):', response.data);
    } catch (error: any) {
      console.log('Erro capturado no axios:', error);
      if (error.response && error.response.data) {
        console.log('Dados do erro:', error.response.data);
      }
      throw error;
    }
  },

  async incluirProduto(dto: IncluirProdutoBalancoDto, empresaId: number): Promise<{ data: any; isSuccess: boolean; message?: string }> {
    try {
      const response = await axios.post("/api/balanco/produtos", dto, createConfig(empresaId));
      return response.data;
    } catch (error: any) {
      console.log('Erro capturado no axios (incluirProduto):', error);
      if (error.response && error.response.data) {
        console.log('Dados do erro:', error.response.data);
        return error.response.data;
      }
      throw error;
    }
  },

  async incluirProdutoPorCodigoBarras(codigoBarras: string, empresaId: number): Promise<ProdutoParaBalancoType> {
    try {
      const response = await axios.post("/api/balanco/produtos/codigo-barras", { codigoBarras }, createConfig(empresaId));
      return response.data;
    } catch (error: any) {
      console.log('Erro capturado no axios (incluirProdutoPorCodigoBarras):', error);
      if (error.response && error.response.data) {
        console.log('Dados do erro:', error.response.data);
        return error.response.data;
      }
      throw error;
    }
  },

  async atualizarProduto(dto: AtualizarProdutoBalancoDto, empresaId: number): Promise<{ isSuccess: boolean; data: any; message?: string }> {
    try {
      const response = await axios.put("/api/balanco/produtos", dto, createConfig(empresaId));
      return response.data;
    } catch (error: any) {
      console.log('Erro capturado no axios (atualizarProduto):', error);
      if (error.response && error.response.data) {
        console.log('Dados do erro:', error.response.data);
        return error.response.data;
      }
      throw error;
    }
  },

  async excluirProduto(dto: ExcluirProdutoBalancoDto, empresaId: number): Promise<{ isSuccess: any; data: any; message?: string }> {
    try {
      const config = createConfig(empresaId);
      const response = await axios.delete("/api/balanco/produtos", {
        ...config,
        data: dto
      });
      return response.data;
    } catch (error: any) {
      console.log('Erro capturado no axios (excluirProduto):', error);
      if (error.response && error.response.data) {
        console.log('Dados do erro:', error.response.data);
        return error.response.data;
      }
      throw error;
    }
  },

  async pesquisarProdutosInfinito(pageParam: number, termo: string | undefined, empresaId: number): Promise<{
    isSuccess: boolean;
    data: ProdutoParaBalancoType[];
    total: number;
    pageIndex: number;
    pageSize: number;
    message?: string
  }> {
    const params = new URLSearchParams();
    if (termo) params.append('termo', termo);
    params.append('pageIndex', pageParam.toString());
    params.append('pageSize', '20'); // Aumentar pageSize para melhor performance

    const response = await axios.get(`/api/balanco/produtos/pesquisar?${params.toString()}`, createConfig(empresaId));
    return response.data;
  },

  async finalizarBalanco(zerarEstoqueDto: ZerarEstoqueDto | null, empresaId: number): Promise<{
    isSuccess: any; data: any; message?: string
  }> {
    try {
      console.log('Enviando para backend:', zerarEstoqueDto, 'EmpresaId:', empresaId);
      if (zerarEstoqueDto) {
        const response = await axios.post("/api/balanco/finalizar", zerarEstoqueDto, createConfig(empresaId));
        return response.data;
      }

      const response = await axios.post("/api/balanco/finalizar", {}, createConfig(empresaId));
      return response.data;
    } catch (error: any) {
      console.log('Erro capturado no axios (finalizarBalanco):', error);
      if (error.response && error.response.data) {
        console.log('Dados do erro:', error.response.data);
        return error.response.data;
      }
      throw error;
    }
  },

  async obterDepartamentos(empresaId: number): Promise<DepartamentoType[]> {
    const response = await axios.get("/api/balanco/departamentos", createConfig(empresaId));
    return response.data;
  },


}; 